import React from 'react';
import { Grid, Paper, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import TimelineIcon from '@mui/icons-material/Timeline';

const SummaryCard = ({ title, value, subtitle, icon, trend, color }) => {
  const isPositiveTrend = trend >= 0;
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        borderRadius: 2, 
        height: '100%',
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Box sx={{ 
          bgcolor: `${color}.100`, 
          color: `${color}.main`, 
          borderRadius: '50%', 
          p: 0.8,
          display: 'flex'
        }}>
          {icon}
        </Box>
      </Box>
      
      <Typography variant="h4" sx={{ fontWeight: 'medium', mb: 1 }}>
        {value}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {trend !== undefined && (
          <>
            {isPositiveTrend ? 
              <TrendingUpIcon sx={{ color: 'success.main', mr: 0.5, fontSize: '1rem' }} /> : 
              <TrendingDownIcon sx={{ color: 'error.main', mr: 0.5, fontSize: '1rem' }} />
            }
            <Typography 
              variant="caption" 
              sx={{ 
                color: isPositiveTrend ? 'success.main' : 'error.main',
                fontWeight: 'medium',
                mr: 0.5
              }}
            >
              {Math.abs(trend).toFixed(1)}%
            </Typography>
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
};

const AnalyticsSummary = ({ data }) => {
  // Check if data is valid and has the required structure
  if (!data || !data.dashboard || !data.dashboard.memberMetrics || !data.dashboard.financialMetrics) {
    // Return placeholder cards when data is not available
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Total Members" 
            value="--"
            subtitle="loading data"
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Total Revenue" 
            value="--"
            subtitle="loading data"
            icon={<MonetizationOnIcon />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Satisfaction Rate" 
            value="--"
            subtitle="loading data"
            icon={<SentimentSatisfiedAltIcon />}
            color="warning"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="NPS Score" 
            value="--"
            subtitle="loading data"
            icon={<TimelineIcon />}
            color="info"
          />
        </Grid>
      </Grid>
    );
  }
  
  const { memberMetrics, financialMetrics } = data.dashboard;
  const { satisfaction, nps } = data.engagement || {};
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard 
          title="Total Members" 
          value={memberMetrics.totalMembers.toLocaleString()}
          subtitle="vs previous period"
          trend={memberMetrics.growthRate}
          icon={<PeopleIcon />}
          color="primary"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard 
          title="Total Revenue" 
          value={`$${financialMetrics.totalRevenue.toLocaleString()}`}
          subtitle="vs previous period"
          trend={financialMetrics.revenueGrowth}
          icon={<MonetizationOnIcon />}
          color="success"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard 
          title="Satisfaction Rate" 
          value={`${satisfaction ? satisfaction.toFixed(1) : 0}%`}
          subtitle="customer satisfaction"
          icon={<SentimentSatisfiedAltIcon />}
          color="warning"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard 
          title="NPS Score" 
          value={nps || 0}
          subtitle="net promoter score"
          icon={<TimelineIcon />}
          color="info"
        />
      </Grid>
    </Grid>
  );
};

export default AnalyticsSummary;
